/**
 * The people of the Bible.
 *
 * Thirty figures the story turns on: the Twelve, the church that followed them,
 * and ten from the Old Testament.
 *
 * Each profile is written to be read rather than referenced — three specific
 * scenes instead of a summary, one anchor verse, the thing they got wrong, and
 * what they left behind. The `flaw` field is not decoration: the Bible is
 * unusually honest about its heroes' failures, and a profile that airbrushes
 * them is both duller and less true.
 *
 * Where the record is thin — several of the Twelve are barely more than a name
 * in a list — the profiles say so rather than padding with invention, and later
 * church tradition is always marked as tradition rather than presented as
 * scripture.
 *
 * Quoted verses are the World English Bible, public domain, the same
 * translation as the rest of the app.
 *
 * `portrait` drives `CharacterPortrait.jsx`, which draws every figure from one
 * illustration system so the set stays visually consistent. Every value is from
 * a fixed enum; adding a new one means adding it to the drawing first.
 */

export const CHARACTERS = [
  {
    id: 'peter',
    name: 'Peter',
    alsoKnownAs: 'Simon; Simon bar Jonah; Cephas',
    epithet: 'The rock who cracked',
    group: 'The Twelve',
    era: 'c. AD 30 – 60s',
    testament: 'new',
    summary:
      'A Galilean fisherman with a loud mouth and a fast heart, the first of the Twelve to name him the Christ in the Synoptic account and the first to swear he had never met him. He fails more publicly than anyone else in the Gospels and is put in charge anyway. Almost everything the young church did in Jerusalem, it did with him at the front.',
    moments: [
      {
        title: 'Out of the boat',
        ref: 'Matthew 14:28-31',
        text:
          'He asked to come across the water, climbed over the side, and managed a few steps before the wind frightened him and he started to go under. Jesus caught hold of him and asked why he had doubted.',
      },
      {
        title: 'By the courtyard fire',
        ref: 'Luke 22:54-62',
        text:
          'Warming his hands among the high priest’s servants, he told a servant girl and two others that he did not know the man. Luke adds the detail that finishes him — the Lord turned and looked at Peter, and he went outside and wept bitterly.',
      },
      {
        title: 'Breakfast on the shore',
        ref: 'John 21:15-17',
        text:
          'Over fish cooked on coals, Jesus asked him three times whether he loved him, once for each denial. Peter, stung by the third asking, said, ‘Lord, you know everything. You know that I have affection for you.’',
      },
    ],
    verse: { ref: 'Matthew 16:16', text: 'You are the Christ, the Son of the living God.' },
    flaw:
      'His courage always outran his nerve. Nearly twenty years after the courtyard, Paul had to confront him at Antioch for quietly withdrawing from Gentile tables once men from James arrived (Galatians 2:11-14) — the same fear of the room, wearing better clothes.',
    legacy:
      'Scripture puts him at the centre of the first decade of the church — the Pentecost sermon, the healing at the temple gate, and the decision at Cornelius’s house that Gentiles need not become Jews first (Acts 2, 3, 10). Two New Testament letters carry his name, though the authorship of the second has been questioned since ancient times. John 21:18-19 hints at a death by which he would glorify God; the specifics — Rome under Nero, crucifixion, head downward — come from later church tradition, not from the Bible.',
    portrait: {
      skin: 'tan',
      hair: 'short',
      hairColour: 'grey',
      beard: 'full',
      headwear: 'none',
      robe: 'ochre',
      emblem: 'keys',
    },
  },
  {
    id: 'andrew',
    name: 'Andrew',
    alsoKnownAs: 'Simon Peter’s brother',
    epithet: 'Always bringing someone along',
    group: 'The Twelve',
    era: 'c. AD 30',
    testament: 'new',
    summary:
      'A fisherman from Bethsaida who had already gone looking — he was following John the Baptist when he first heard Jesus described. Every scene John’s Gospel gives him has the same shape: he finds somebody and steers them towards Jesus. He is also, permanently, the brother of a more famous man.',
    moments: [
      {
        title: 'The tenth hour',
        ref: 'John 1:35-39',
        text:
          'He heard his teacher point at a passing stranger and left to follow him, then asked awkwardly where he was staying. He was invited to come and see, and stayed the rest of the day — the writer still remembers it was about four in the afternoon.',
      },
      {
        title: 'Fetching his brother',
        ref: 'John 1:41-42',
        text:
          'The first thing he did was go and find Simon and tell him they had found the Messiah. He brought him to Jesus, and Jesus renamed him on the spot.',
      },
      {
        title: 'A boy with five loaves',
        ref: 'John 6:8-9',
        text:
          'Faced with a hungry crowd, he produced a boy carrying five barley loaves and two fish, then undercut himself: ‘but what are these among so many?’ It is the only food the scene mentions.',
      },
    ],
    verse: { ref: 'John 1:41', text: 'We have found the Messiah!' },
    flaw:
      'No failing of his is written down. What scripture does record is the shape of his life: he is introduced as Simon Peter’s brother and stays in that shadow, and we are never told what it cost him — or whether it cost him anything at all.',
    legacy:
      'The Gospels record him bringing his brother, a boy with lunch, and a group of curious Greeks (John 12:20-22), and once sitting with the inner three to ask about the temple’s fall (Mark 13:3). After Acts 1:13 the Bible says nothing more. Crucifixion at Patras on an X-shaped cross, and his patronage of Scotland, Russia and Greece, are later tradition rather than scriptural record.',
    portrait: {
      skin: 'tan',
      hair: 'long',
      hairColour: 'grey',
      beard: 'long',
      headwear: 'none',
      robe: 'sand',
      emblem: 'fish',
    },
  },
  {
    id: 'james-son-of-zebedee',
    name: 'James son of Zebedee',
    alsoKnownAs: 'James the Great; one of the Sons of Thunder',
    epithet: 'Thunder, and the first to fall',
    group: 'The Twelve',
    era: 'c. AD 30 – 44',
    testament: 'new',
    summary:
      'Brother of John, and half of the pair Jesus nicknamed Boanerges — Sons of Thunder. (Scripture lists James first but never says which was the elder.) He is in the room for everything the inner three see, and he is hot-tempered enough to suggest burning down a village. He is the only apostle whose execution the New Testament actually reports.',
    moments: [
      {
        title: 'Nets left mid-repair',
        ref: 'Mark 1:19-20',
        text:
          'He and John were in the boat mending nets with their father when Jesus called them. They walked out and left Zebedee sitting there with the hired men.',
      },
      {
        title: 'Fire on the village',
        ref: 'Luke 9:51-56',
        text:
          'When a Samaritan village refused to host them, he and his brother asked whether they should call fire down from heaven on it. Jesus turned and rebuked them, and they walked on to another village.',
      },
      {
        title: 'The seats they wanted',
        ref: 'Mark 10:35-40',
        text:
          'The brothers asked for the places at Jesus’ right and left in his glory. He told them they did not know what they were asking, and that they would drink the cup he drank — which, for James, turned out to be exact.',
      },
    ],
    verse: { ref: 'Acts 12:2', text: 'He killed James, the brother of John, with the sword.' },
    flaw:
      'He wanted rank, and he wanted revenge on people who would not put him up for the night. The Gospels keep both stories in, and neither is softened by the fact that he died first.',
    legacy:
      'Herod Agrippa I had him executed in Jerusalem around AD 44, making him the first of the Twelve to die for the name and the only one whose martyrdom the Bible records — in a single flat sentence, with no last words. The medieval traditions that carried his bones to Compostela in Spain and made him the patron of pilgrims are exactly that: traditions, formed centuries later.',
    portrait: {
      skin: 'olive',
      hair: 'curly',
      hairColour: 'black',
      beard: 'short',
      headwear: 'kaffiyeh',
      robe: 'wine',
      emblem: 'sword',
    },
  },
  {
    id: 'john',
    name: 'John',
    alsoKnownAs: 'John son of Zebedee; traditionally ‘the disciple whom Jesus loved’',
    epithet: 'Son of thunder, last man standing',
    group: 'The Twelve',
    era: 'c. AD 30 – 90s',
    testament: 'new',
    summary:
      'The other Zebedee brother, present at every scene reserved for the inner three, and just as ready as his brother to call down fire. In Acts he is Peter’s shadow before the Sanhedrin; in Galatians, Paul calls him a pillar of the Jerusalem church. More of the New Testament is traditionally laid at his door than at any other member of the Twelve.',
    moments: [
      {
        title: 'Stopping the outsider',
        ref: 'Mark 9:38-40',
        text:
          'He reported, apparently expecting approval, that they had stopped a man casting out demons in Jesus’ name because he was not one of their group. Jesus told him not to — whoever is not against us is for us.',
      },
      {
        title: 'Woman, behold your son',
        ref: 'John 19:26-27',
        text:
          'The disciple whom Jesus loved — whom tradition identifies as John — stood near the cross with Mary. Jesus gave the two of them to each other, and from that hour the disciple took her into his own home.',
      },
      {
        title: 'Unschooled, before the council',
        ref: 'Acts 4:13',
        text:
          'Hauled up with Peter before the Jerusalem authorities, the two of them refused to stop speaking. The council noted their boldness and that they were uneducated, ordinary men, and recognised that they had been with Jesus.',
      },
    ],
    verse: { ref: 'John 19:27', text: 'Then he said to the disciple, “Behold, your mother!”' },
    flaw:
      'For all the later reputation for love, the John of the Gospels is territorial and status-hungry: he wants a throne beside Jesus, wants a village punished for poor hospitality, and wants outsiders barred from using the name.',
    legacy:
      'Church tradition credits him with the Fourth Gospel, three letters and Revelation; the Gospel and letters name no author, Revelation names a John exiled on Patmos (Revelation 1:9), and scholars have disagreed for centuries about how many Johns are involved. Tradition also holds that he alone among the Twelve died of old age, at Ephesus — an old and widespread claim, but not a biblical one.',
    portrait: {
      skin: 'olive',
      hair: 'long',
      hairColour: 'brown',
      beard: 'none',
      headwear: 'none',
      robe: 'indigo',
      emblem: 'chalice',
    },
  },
  {
    id: 'philip',
    name: 'Philip',
    alsoKnownAs: 'Philip of Bethsaida',
    epithet: 'The man who did the arithmetic',
    group: 'The Twelve',
    era: 'c. AD 30',
    testament: 'new',
    summary:
      'A Bethsaida man who appears only in John’s Gospel with any substance, and always as the practical one — costing out the catering, fielding enquiries from strangers, asking for something he can actually see. He recruits Nathanael with three words and then spends three years slightly missing the point.',
    moments: [
      {
        title: 'Come and see',
        ref: 'John 1:43-46',
        text:
          'He found Nathanael and told him they had found the man Moses wrote about — Jesus of Nazareth. When Nathanael sneered at the town, Philip did not argue; he said, ‘Come and see.’',
      },
      {
        title: 'Two hundred denarii',
        ref: 'John 6:5-7',
        text:
          'Asked where they could buy bread for the crowd, he answered with a sum: eight months’ wages would not be enough for each of them to get a little. The question had been a test, and he had reached for his accounts.',
      },
      {
        title: 'Show us the Father',
        ref: 'John 14:8-9',
        text:
          'At the last supper he asked, reasonably enough, for one look at the Father. Jesus answered with something close to grief — have I been with you so long, and you still do not know me, Philip?',
      },
    ],
    verse: { ref: 'John 14:8', text: 'Lord, show us the Father, and that will be enough for us.' },
    flaw:
      'He measures before he trusts. Given a miracle to look at, he asks for a demonstration; given an impossible crowd, he reaches for the ledger and concludes the thing cannot be done.',
    legacy:
      'He is the disciple that curious foreigners approach — the Greeks at the festival come to him first (John 12:20-22) — which may be why he keeps his Greek name in the record. He should not be confused with Philip the Evangelist of Acts 6 and 8, a different man; the two were already being mixed up in the early centuries. Traditions place his later work and death at Hierapolis in Asia Minor, but scripture says nothing of either.',
    portrait: {
      skin: 'light',
      hair: 'receding',
      hairColour: 'brown',
      beard: 'short',
      headwear: 'cap',
      robe: 'olive',
      emblem: 'bread',
    },
  },
  {
    id: 'bartholomew',
    name: 'Bartholomew',
    alsoKnownAs: 'Nathanael (traditionally identified as the same man); Bar-Tolmai, ‘son of Tolmai’',
    epithet: 'Sceptic under a fig tree',
    group: 'The Twelve',
    era: 'c. AD 30',
    testament: 'new',
    summary:
      'One of the four apostles about whom scripture is nearly silent. The Synoptic Gospels and Acts list a Bartholomew and say nothing else about him; John never mentions the name, but gives us Nathanael of Cana, who arrives with an insult on his lips and leaves convinced. Christians since at least the ninth century have assumed they are one person, and they may well be right — but the Bible never says so.',
    moments: [
      {
        title: 'Anything good from Nazareth?',
        ref: 'John 1:45-46',
        text:
          'Told that the man from Moses and the prophets had turned up, and that he came from Nazareth, Nathanael asked whether anything good could come out of that place. Philip declined to defend the town and simply told him to come and look.',
      },
      {
        title: 'Under the fig tree',
        ref: 'John 1:47-49',
        text:
          'Jesus greeted him as a true Israelite with no deceit in him, and said he had seen him under the fig tree before Philip ever called. Whatever that meant to Nathanael, it undid him: he answered that this was the Son of God, the King of Israel.',
      },
      {
        title: 'A name in the list',
        ref: 'Mark 3:18',
        text:
          'Bartholomew is named among the Twelve here and in the three other lists, and then never again in any scene, sermon or saying. Four mentions of a name is the whole of the biblical record.',
      },
    ],
    verse: { ref: 'John 1:47', text: 'Behold, an Israelite indeed, in whom is no deceit!' },
    flaw:
      'His first recorded words are a slur against a small town, the kind of reflex contempt that decides in advance what a place can produce. He was, at least, willing to walk over and be proved wrong.',
    legacy:
      'If Bartholomew is Nathanael, we know he came from Cana and was in the boat at the last resurrection appearance in John 21:2. If he is not, we know only his father’s name. Later traditions send him to Armenia, India or Persia and describe a death by flaying — the source of his gruesome place in medieval art — but these are traditions of varying age and reliability, with no biblical support at all.',
    portrait: {
      skin: 'olive',
      hair: 'curly',
      hairColour: 'brown',
      beard: 'full',
      headwear: 'turban',
      robe: 'ivory',
      emblem: 'scroll',
    },
  },
  {
    id: 'thomas',
    name: 'Thomas',
    alsoKnownAs: 'Didymus (‘the Twin’)',
    epithet: 'Braver than his nickname',
    group: 'The Twelve',
    era: 'c. AD 30',
    testament: 'new',
    summary:
      'Remembered for one week of scepticism and forgotten for everything else, including the day he volunteered to die. He asks the questions the others are too embarrassed to ask, states his terms plainly, and ends up making the sharpest confession of Jesus’ identity in the whole of John’s Gospel.',
    moments: [
      {
        title: 'Let us go and die',
        ref: 'John 11:14-16',
        text:
          'When Jesus announced he was returning to Judea, where a crowd had recently tried to stone him, the group hesitated. Thomas said to the others, ‘Let’s go also, that we may die with him,’ and they went.',
      },
      {
        title: 'We don’t know the way',
        ref: 'John 14:5',
        text:
          'Jesus said they knew where he was going and knew the way. Thomas said flatly that they did not know either — a contradiction that produced one of the most quoted answers in the Gospels.',
      },
      {
        title: 'Eight days later',
        ref: 'John 20:24-28',
        text:
          'He had missed the first appearance and refused to take it second-hand, naming the exact evidence he required: the nail marks, under his own finger. When Jesus came and offered him precisely that, the text never says he touched anything — only that he said, ‘My Lord and my God!’',
      },
    ],
    verse: { ref: 'John 20:28', text: 'My Lord and my God!' },
    flaw:
      'He set conditions on belief and made them non-negotiable, and he was not with the others when it mattered — his absence, not his mind, is what left him a week behind everyone else.',
    legacy:
      '‘Doubting Thomas’ has outlived the man and flattened him; the same Gospel that records the doubt records the courage and the confession. The Christians of Kerala in south India trace their founding to his preaching, a tradition of real antiquity though not one scripture attests. The Gospel of Thomas and the Acts of Thomas are later works that borrowed his name; he did not write them.',
    portrait: {
      skin: 'tan',
      hair: 'short',
      hairColour: 'black',
      beard: 'stubble',
      headwear: 'none',
      robe: 'slate',
      emblem: 'staff',
    },
  },
  {
    id: 'matthew',
    name: 'Matthew',
    alsoKnownAs: 'Levi (traditionally identified as the same man); Levi son of Alphaeus',
    epithet: 'Collaborator, called mid-shift',
    group: 'The Twelve',
    era: 'c. AD 30',
    testament: 'new',
    summary:
      'A tax collector at the Capernaum booth — a Jew taking toll money from Jews for Herod Antipas, Rome’s client ruler, and for himself — and despised for it by everyone who walked past. Jesus called him at his desk, and he threw a large dinner party for exactly the sort of people the religious authorities avoided. Mark and Luke tell the same story of a call at the booth but name the man Levi; the list in Matthew’s Gospel is the only one that keeps the job title attached to his name.',
    moments: [
      {
        title: 'At the tax booth',
        ref: 'Matthew 9:9',
        text:
          'Jesus walked past the customs post where he sat collecting, said two words to him, and kept walking. He got up and went, leaving the table and the takings behind.',
      },
      {
        title: 'The wrong guest list',
        ref: 'Luke 5:29-30',
        text:
          'He put on a great feast at his house, and the room filled with tax collectors and other people of poor reputation. The Pharisees and scribes complained to the disciples about the company their teacher was keeping.',
      },
      {
        title: 'Mercy, not sacrifice',
        ref: 'Matthew 9:12-13',
        text:
          'Jesus answered the complaint with a line about doctors going where the sick are, and sent his critics back to Hosea: ‘I desire mercy, and not sacrifice.’ The argument was made over Matthew’s own dinner table.',
      },
    ],
    verse: { ref: 'Matthew 9:9', text: 'He said to him, “Follow me.” He got up and followed him.' },
    flaw:
      'He made his living from the occupation of his own country, and the Gospels never soften it or explain it away. Zacchaeus paid his victims back; scripture does not tell us that Matthew ever did.',
    legacy:
      'The identification of Matthew with the Levi of Mark 2:14 and Luke 5:27 is an early and near-universal reading, but no text makes it explicitly. Church tradition since at least the second century has attributed the First Gospel to him, though the book itself names no author and modern scholars are divided about the connection. His name became shorthand for the possibility that a person could be called out of a genuinely disgraceful trade without first being made respectable. The Bible records nothing of his later life or death; the traditions that do — Ethiopia, Persia, martyrdom — disagree with one another.',
    portrait: {
      skin: 'light',
      hair: 'short',
      hairColour: 'brown',
      beard: 'short',
      headwear: 'cap',
      robe: 'clay',
      emblem: 'coin-purse',
    },
  },
  {
    id: 'james-son-of-alphaeus',
    name: 'James son of Alphaeus',
    alsoKnownAs: 'Traditionally identified with ‘James the less’ of Mark 15:40',
    epithet: 'A name on the list',
    group: 'The Twelve',
    era: 'c. AD 30',
    testament: 'new',
    summary:
      'One of the Twelve about whom the Bible records nothing but a name and a father’s name, repeated four times. He is present at the calling, present after the resurrection, and silent throughout. The honest account of him is short, and padding it would mean making things up.',
    moments: [
      {
        title: 'Appointed on the mountain',
        ref: 'Mark 3:13-18',
        text:
          'Jesus went up the mountain, called the men he wanted, and appointed twelve to be with him and to be sent out. James son of Alphaeus is the ninth name read out, and that is all the scene tells us.',
      },
      {
        title: 'Waiting in the room',
        ref: 'Acts 1:13-14',
        text:
          'After the ascension, the surviving eleven are named as they go up to the upper room in Jerusalem, where they wait and pray with the women and with Jesus’ mother. He is named there — still present, still silent.',
      },
      {
        title: 'Another James, at the cross',
        ref: 'Mark 15:40',
        text:
          'Among the women watching the crucifixion from a distance is Mary, ‘the mother of James the less and of Joses’. Later tradition identified that James with this one; the text itself makes no such connection.',
      },
    ],
    verse: { ref: 'Mark 3:14', text: 'He appointed twelve, that they might be with him.' },
    flaw:
      'No failing of his is recorded, because nothing of his is recorded. The honest thing is to leave the space empty rather than invent a temperament for a man who left us only a name.',
    legacy:
      'Everything beyond the four lists is tradition or guesswork. He shares a patronymic with Levi ‘son of Alphaeus’ in Mark 2:14, but scripture never says they were brothers. Some traditions merge him with James the brother of Jesus, who led the Jerusalem church and appears in Acts 15 and Galatians — most historians treat these as two different men. Accounts of his death vary wildly between traditions and none has biblical support.',
    portrait: {
      skin: 'olive',
      hair: 'receding',
      hairColour: 'grey',
      beard: 'full',
      headwear: 'kaffiyeh',
      robe: 'sand',
      emblem: 'anchor',
    },
  },
  {
    id: 'thaddaeus',
    name: 'Thaddaeus',
    alsoKnownAs: 'Judas son of James; Lebbaeus (in some manuscripts); Jude',
    epithet: 'One question, and no more',
    group: 'The Twelve',
    era: 'c. AD 30',
    testament: 'new',
    summary:
      'An apostle the lists do not name consistently — Thaddaeus in Matthew and Mark (Lebbaeus in some manuscripts), Judas son of James in Luke and Acts. Almost everyone since antiquity has taken these for one man, though no text says so; on that reading he speaks exactly once in the New Testament. That one line is a good question, asked at the last supper. Beyond it, the Bible tells us nothing about him at all.',
    moments: [
      {
        title: 'Not that Judas',
        ref: 'Luke 6:16',
        text:
          'Luke’s list names him Judas the son of James, and then immediately names Judas Iscariot, ‘who became a traitor’. Matthew and Mark call the same man Thaddaeus, apparently to keep the two apart.',
      },
      {
        title: 'Why not the world?',
        ref: 'John 14:22',
        text:
          'In the middle of the last supper he interrupted to ask why Jesus was about to show himself to them and not to everybody. It is his only recorded sentence, and the Gospel gives no hint of what prompted it.',
      },
      {
        title: 'Counted among the eleven',
        ref: 'Acts 1:13',
        text:
          'He goes up to the upper room with the rest after the ascension, named again as Judas son of James. That is the last the New Testament sees of him.',
      },
    ],
    verse: { ref: 'John 14:22', text: 'Lord, what has happened that you are about to reveal yourself to us, and not to the world?' },
    flaw:
      'Nothing against him is written down. What the record does show is a man defined by a negative — the Gospels take care to identify him as the Judas who was not the traitor, which is a hard way to be remembered.',
    legacy:
      'The letter of Jude opens as from ‘Jude, a servant of Jesus Christ, and brother of James’; whether that is this apostle or Jesus’ own brother has been argued since antiquity, and the letter does not settle it. Traditions pair him with Simon the Zealot on a mission to Persia and a shared martyrdom, but they are late and inconsistent. His modern role as patron of hopeless causes is a devotional tradition, commonly explained by the fact that his name was avoided for centuries because of the other Judas.',
    portrait: {
      skin: 'tan',
      hair: 'curly',
      hairColour: 'black',
      beard: 'full',
      headwear: 'hood',
      robe: 'olive',
      emblem: 'fire',
    },
  },
  {
    id: 'simon-the-zealot',
    name: 'Simon the Zealot',
    alsoKnownAs: 'Simon the Cananaean',
    epithet: 'A nickname doing all the work',
    group: 'The Twelve',
    era: 'c. AD 30',
    testament: 'new',
    summary:
      'Named four times, always with the epithet that separates him from Simon Peter, and never given a line or a scene. The label points to zeal — Cananaean is the Aramaic behind the Greek ‘Zealot’ — but what it meant for him is genuinely uncertain. Everything vivid about him is supplied by readers, not by scripture.',
    moments: [
      {
        title: 'The zealot in the list',
        ref: 'Luke 6:15',
        text:
          'Luke names him Simon who was called the Zealot; Matthew and Mark use the Aramaic form, Cananaean. The word can mean a member of an armed faction or simply a man fiercely committed to the Law, and the text does not say which.',
      },
      {
        title: 'Beside the tax collector',
        ref: 'Matthew 10:3-4',
        text:
          'In the same list, three places apart, stands Matthew the tax collector — a man who collected for Rome’s client ruler. Whatever Simon’s zeal amounted to, he was expected to travel and eat with him.',
      },
      {
        title: 'In the upper room',
        ref: 'Acts 1:13',
        text:
          'He is named among the eleven waiting in Jerusalem after the ascension, still carrying the nickname. Then the New Testament closes on him for good.',
      },
    ],
    verse: { ref: 'Luke 6:15', text: 'Simon who was called the Zealot' },
    flaw:
      'Scripture records no fault, and gives us no scene in which to look for one. The temptation with Simon is to hand him a hidden dagger and a political grudge because his nickname invites it; that is fiction, and worth resisting.',
    legacy:
      'The organised Zealot party that drove the revolt against Rome took shape decades after the events the Gospels describe, so his nickname may describe temperament rather than membership; historians disagree, and the Gospels do not clarify. No word of his, no action of his and no place of his is recorded. Traditions send him to Persia, Egypt or even Britain and describe his death by being sawn in two — the origin of the saw in his artwork — but they contradict one another and none is biblical.',
    portrait: {
      skin: 'deep',
      hair: 'short',
      hairColour: 'black',
      beard: 'short',
      headwear: 'kaffiyeh',
      robe: 'clay',
      emblem: 'saw',
    },
  },
  {
    id: 'judas-iscariot',
    name: 'Judas Iscariot',
    alsoKnownAs: 'Judas son of Simon Iscariot',
    epithet: 'The one who used a kiss',
    group: 'The Twelve',
    era: 'c. AD 30 – 33',
    testament: 'new',
    summary:
      'One of the Twelve, trusted enough to carry the group’s money, who agreed a price for handing Jesus over and identified him in the dark with an embrace. He was not a stranger who infiltrated the group; he was a colleague of three years. The Gospels report what he did, Matthew alone reports that he regretted it, and none of them gives a motive that accounts for it.',
    moments: [
      {
        title: 'The perfume and the purse',
        ref: 'John 12:4-6',
        text:
          'When Mary poured out costly ointment, he objected that it should have been sold for the poor — a year’s wages wasted. John, writing much later, adds that he kept the money box and helped himself to what was in it.',
      },
      {
        title: 'And it was night',
        ref: 'John 13:26-30',
        text:
          'At supper Jesus handed him a piece of bread and told him to do quickly what he was doing; the others assumed he had been sent out to buy something for the festival. He took the bread, went out, and John ends the sentence: and it was night.',
      },
      {
        title: 'The agreed signal',
        ref: 'Mark 14:44-45',
        text:
          'He had told the arresting party that the one he kissed was the man they wanted. He came straight up to Jesus in the olive grove, said ‘Rabbi!’, and kissed him.',
      },
    ],
    verse: { ref: 'Matthew 27:4', text: 'I have sinned in that I betrayed innocent blood.' },
    flaw:
      'John names theft; Luke 22:3 says Satan entered him; nobody in the Gospels offers a motive that fully accounts for it. Read plainly, he is a man who managed the accounts, watched everything the others watched, and then decided thirty pieces of silver was a fair price — and who, when the sentence came down, could not live with what he had done.',
    legacy:
      'His name became a byword for treachery within a generation, and has stayed one. The New Testament gives two accounts of his end that do not match easily: Matthew 27:3-5 has him fling the silver into the temple and hang himself, while Acts 1:18-19 has him buy a field and die there, the place afterwards called Akeldama, Field of Blood. Acts 1:15-26 records the eleven casting lots to replace him, and choosing Matthias.',
    portrait: {
      skin: 'olive',
      hair: 'long',
      hairColour: 'black',
      beard: 'stubble',
      headwear: 'none',
      robe: 'slate',
      emblem: 'lamp',
    },
  },
  {
    id: 'mary-mother-of-jesus',
    name: 'Mary the mother of Jesus',
    alsoKnownAs: 'Miriam; Mary of Nazareth',
    epithet: 'Stayed when the others fled',
    group: 'The Early Church',
    era: 'c. 5 BC – AD 30s',
    testament: 'new',
    summary:
      'A young woman in an obscure hill-country village agreed to a pregnancy that would wreck her reputation and could have cost her her marriage, then spent the next thirty years watching what came of it. The Gospels show her puzzled at least as often as certain — storing up sayings she could not yet explain, losing her twelve-year-old in Jerusalem, prodding her son into his first miracle. She is one of the few people present at both the start of the story and its end.',
    moments: [
      {
        title: 'The messenger in Nazareth',
        ref: 'Luke 1:26–38',
        text:
          'Gabriel tells her she will bear a son. Her first response is not worship but a practical objection — she is not yet living with a man — and only after the answer does she consent.',
      },
      {
        title: 'They have no wine',
        ref: 'John 2:1–11',
        text:
          'At a village wedding the drink runs out and she simply informs him. He protests that his hour has not come; she turns to the servants and says, “Whatever he says to you, do it.”',
      },
      {
        title: 'Standing at the cross',
        ref: 'John 19:25–27',
        text:
          'Most of his followers had scattered. She stayed close enough to hear him, and he used some of his last breath to place her in another man\'s household.',
      },
    ],
    verse: { ref: 'Luke 1:46', text: 'Mary said, “My soul magnifies the Lord.”' },
    flaw:
      'Mark 3 has his mother and brothers standing outside a crowded house sending word for him, only to hear him say that whoever does God\'s will is his family, and Luke states flatly that she did not understand what her son told her in the temple.',
    legacy:
      'Scripture\'s last glimpse of her is unglamorous and telling: Acts 1:14 puts her in the upper room praying with the others, one believer among many. Almost everything else said about her — perpetual virginity, her death or assumption, her titles — belongs to later church tradition and differs sharply between Christian traditions; the Gospels themselves say nothing of it.',
    portrait: {
      skin: 'olive',
      hair: 'long',
      hairColour: 'brown',
      beard: 'none',
      headwear: 'veil',
      robe: 'indigo',
      emblem: 'star',
    },
  },
  {
    id: 'mary-magdalene',
    name: 'Mary Magdalene',
    alsoKnownAs: 'Mary of Magdala',
    epithet: 'First at the empty tomb',
    group: 'The Early Church',
    era: 'c. AD 30',
    testament: 'new',
    summary:
      'A woman from a fishing town on the Sea of Galilee out of whom, Luke says, seven demons had gone. She joined the travelling company and helped pay for it out of her own money, followed the execution to its end when most of the men had gone, and became the first person to see Jesus alive again — with the job of telling the others, who did not believe her.',
    moments: [
      {
        title: 'Paying the bills',
        ref: 'Luke 8:1–3',
        text:
          'Luke lists her first among the women who travelled with Jesus and the Twelve and supported them from their own means. The mission ran on the purses of women whose names most readers skip.',
      },
      {
        title: 'Watching where they laid him',
        ref: 'Mark 15:40–47',
        text:
          'She watched the crucifixion from a distance and then watched Joseph of Arimathea roll the stone across, noting the place. It was practical grief — she meant to come back with spices.',
      },
      {
        title: 'Mistaking him for the gardener',
        ref: 'John 20:11–18',
        text:
          'Weeping outside the tomb, she asks a stranger where the body has been taken. He says her name, and she recognises the voice before the face.',
      },
    ],
    verse: { ref: 'John 20:16', text: 'Jesus said to her, “Mary.”' },
    flaw:
      'She went to the tomb carrying spices, which is to say that after everything she had been told, she fully expected to be anointing a corpse.',
    legacy:
      'Early writers called her apostle to the apostles — the one sent to those who were sent. The familiar picture of her as a reformed prostitute comes from a later Western conflation of three separate women; no text calls her one, and the medieval legends of her old age in France are legend, not record.',
    portrait: {
      skin: 'tan',
      hair: 'long',
      hairColour: 'auburn',
      beard: 'none',
      headwear: 'none',
      robe: 'clay',
      emblem: 'water-jar',
    },
  },
  {
    id: 'john-the-baptist',
    name: 'John the Baptist',
    alsoKnownAs: 'John the Baptiser; the Forerunner',
    epithet: 'Locusts, honey, and no flattery',
    group: 'The Early Church',
    era: 'c. 5 BC – c. AD 29',
    testament: 'new',
    summary:
      'A priest\'s son who left the temple system for the desert, wore camel hair, ate insects, and told crowds who walked miles to hear him that they were a brood of vipers. He drew soldiers, tax collectors and Pharisees into the Jordan, baptised his own cousin, and then deliberately talked himself out of a following. He kept the habit of saying the unwelcome thing right up to the point where it cost him his head.',
    moments: [
      {
        title: 'A voice in the wilderness',
        ref: 'Mark 1:4–6',
        text:
          'He preached at the Jordan in camel hair and a leather belt, living on locusts and wild honey. People came out from Jerusalem to be insulted and then baptised.',
      },
      {
        title: 'Baptising the greater man',
        ref: 'Matthew 3:13–17',
        text:
          'When Jesus queued up with everyone else, John tried to refuse — he wanted it the other way round — and gave way only when told to.',
      },
      {
        title: 'A question from prison',
        ref: 'Matthew 11:2–6',
        text:
          'Locked up by Herod, he sent his own disciples to ask whether Jesus was really the one, or whether they should look for somebody else. The man who had pointed him out was no longer sure.',
      },
    ],
    verse: { ref: 'John 3:30', text: 'He must increase, but I must decrease.' },
    flaw:
      'He had announced Jesus as the Lamb of God and then, from a cell, sent word asking whether he had got it wrong.',
    legacy:
      'His death is one of the rare martyrdoms scripture actually narrates: imprisoned for telling Herod Antipas that his marriage to Herodias was unlawful, beheaded on a whim at a birthday feast. The Jewish historian Josephus, writing outside the New Testament, also records his execution — one of the few early Christian figures corroborated that way.',
    portrait: {
      skin: 'tan',
      hair: 'long',
      hairColour: 'black',
      beard: 'long',
      headwear: 'none',
      robe: 'ochre',
      emblem: 'staff',
    },
  },
  {
    id: 'paul',
    name: 'Paul',
    alsoKnownAs: 'Saul of Tarsus',
    epithet: 'Held the coats, then preached',
    group: 'The Early Church',
    era: 'c. AD 5 – c. AD 65',
    testament: 'new',
    summary:
      'A well-trained Pharisee from Tarsus who first appears in the story minding the coats of the men stoning Stephen, and who then went house to house dragging believers to prison. Blinded on the Damascus road, he spent the rest of his life walking the Roman east arguing that the God of Israel had opened the door to everyone. He was beaten, jailed, shipwrecked and repeatedly run out of town, and wrote letters between the incidents that shaped Christian thought more than any other human documents.',
    moments: [
      {
        title: 'The Damascus road',
        ref: 'Acts 9:1–9',
        text:
          'Carrying warrants for arrests, he is knocked to the ground by a light and a voice asking why he is persecuting it. He got up blind and had to be led into the city by the hand.',
      },
      {
        title: 'Singing in the stocks',
        ref: 'Acts 16:22–34',
        text:
          'Flogged and locked in the inner cell at Philippi with his feet in stocks, he and Silas were singing at midnight when the earthquake hit. Nobody ran, and the terrified jailer ended up washing their wounds.',
      },
      {
        title: 'Among the philosophers',
        ref: 'Acts 17:22–34',
        text:
          'On the Areopagus in Athens he opened with an altar he had noticed, inscribed to an unknown god, and quoted their own poets back at them. When he reached the resurrection, some of them laughed.',
      },
    ],
    verse: { ref: 'Galatians 2:20', text: 'It is no longer I who live, but Christ lives in me.' },
    flaw:
      'He consented to Stephen\'s killing and hunted believers house to house, and the old temper survived his conversion — his row with Barnabas over John Mark was sharp enough that the two men who built the Gentile mission never worked together again in any record we have.',
    legacy:
      'Thirteen New Testament letters carry his name, and the argument that Gentiles need not become Jews to belong is largely his. Acts leaves him alive under house arrest in Rome, still preaching; his execution under Nero is church tradition rather than biblical record.',
    portrait: {
      skin: 'olive',
      hair: 'receding',
      hairColour: 'grey',
      beard: 'full',
      headwear: 'none',
      robe: 'slate',
      emblem: 'sword',
    },
  },
  {
    id: 'stephen',
    name: 'Stephen',
    alsoKnownAs: 'Stephanos',
    epithet: 'Prayed for his own executioners',
    group: 'The Early Church',
    era: 'c. AD 34',
    testament: 'new',
    summary:
      'A Greek-speaking believer picked to sort out a food distribution row so that widows stopped being overlooked, who turned out to be a formidable debater as well as an administrator. Hauled before the council on charges of speaking against the temple, he answered with a long, unflinching walk through Israel\'s history that ended by accusing his judges. They took him outside and stoned him, and he died asking that it not be held against them.',
    moments: [
      {
        title: 'Chosen to wait tables',
        ref: 'Acts 6:1–6',
        text:
          'The Greek-speaking widows were being missed in the daily hand-out, so seven men were appointed to fix it. Stephen is named first — full of faith, and evidently good with a list.',
      },
      {
        title: 'The speech that killed him',
        ref: 'Acts 7:1–53',
        text:
          'Given a chance to answer the charge, he retold the whole national story — Abraham, Joseph, Moses, the tabernacle — and ended by telling the council they had always resisted the Holy Spirit.',
      },
      {
        title: 'Coats at Saul\'s feet',
        ref: 'Acts 7:54–60',
        text:
          'They dragged him out of the city and the witnesses piled their cloaks at the feet of a young man named Saul. Stephen knelt, asked that the sin not be charged to them, and died.',
      },
    ],
    verse: { ref: 'Acts 7:60', text: 'Lord, don\'t hold this sin against them!' },
    flaw:
      'Scripture records nothing against him, which is rare enough to be worth saying — though his defence gave not an inch, calling the men holding his life in their hands stiff-necked, and it is that speech that gets him killed.',
    legacy:
      'Acts makes him the first believer killed for the name, and his death scatters the Jerusalem church across Judea and Samaria — which is how the message spread. Notably, his martyrdom needs no tradition to supply it: Luke narrates the whole thing, including the bystander who became Paul.',
    portrait: {
      skin: 'olive',
      hair: 'short',
      hairColour: 'black',
      beard: 'short',
      headwear: 'none',
      robe: 'ivory',
      emblem: 'stones',
    },
  },
  {
    id: 'barnabas',
    name: 'Barnabas',
    alsoKnownAs: 'Joseph, a Levite of Cyprus',
    epithet: 'Vouched for the church\'s enemy',
    group: 'The Early Church',
    era: 'c. AD 35–50',
    testament: 'new',
    summary:
      'A Levite landowner from Cyprus whose real name was Joseph; the apostles nicknamed him Son of Encouragement and it stuck. When Saul the persecutor turned up in Jerusalem and everybody sensibly kept their distance, Barnabas took him by the arm and introduced him. He had a career-long habit of standing surety for people others had written off — which is how the church got Paul, and later kept Mark.',
    moments: [
      {
        title: 'The field he sold',
        ref: 'Acts 4:36–37',
        text:
          'He sold a piece of land and laid the money at the apostles\' feet. Luke mentions it as the good example immediately before Ananias and Sapphira make a mess of the same gesture.',
      },
      {
        title: 'Introducing Saul',
        ref: 'Acts 9:26–28',
        text:
          'The Jerusalem believers would not go near the man who had been arresting them. Barnabas took him and brought him to the apostles, and told them what had happened on the road.',
      },
      {
        title: 'Mistaken for Zeus',
        ref: 'Acts 14:8–18',
        text:
          'After a healing at Lystra the crowd decided Barnabas was Zeus and Paul was Hermes, and the local priest turned up with oxen and garlands. The two men tore their clothes and ran into the crowd shouting that they were only human.',
      },
    ],
    verse: { ref: 'Acts 11:24', text: 'For he was a good man, and full of the Holy Spirit and of faith.' },
    flaw:
      'Paul names him in Galatians 2:13 among those who caved at Antioch — even Barnabas withdrew from the table when the Gentiles\' company became awkward.',
    legacy:
      'He put Antioch on its feet and fetched Paul out of obscurity in Tarsus to help; when the two later split over John Mark, Barnabas sailed for Cyprus with the young man nobody else wanted, and Paul in time called Mark useful. His martyrdom on Cyprus is later tradition, not scripture, and the early document known as the Epistle of Barnabas is not his work and is not in the New Testament.',
    portrait: {
      skin: 'tan',
      hair: 'short',
      hairColour: 'grey',
      beard: 'full',
      headwear: 'kaffiyeh',
      robe: 'sand',
      emblem: 'scroll',
    },
  },
  {
    id: 'priscilla',
    name: 'Priscilla',
    alsoKnownAs: 'Prisca',
    epithet: 'Took the great preacher aside',
    group: 'The Early Church',
    era: 'c. AD 49–60',
    testament: 'new',
    summary:
      'A Jewish woman expelled from Rome with her husband Aquila by imperial edict, who set up a tentmaking workshop in Corinth and took in a lodger named Paul who worked the same trade. She and Aquila hosted churches in their house in at least two cities, and when they heard a brilliant preacher teaching something incomplete, they did not correct him in public — they invited him home. Paul says the pair of them once risked their necks for his life.',
    moments: [
      {
        title: 'Tent-cloth in Corinth',
        ref: 'Acts 18:1–3',
        text:
          'Newly arrived refugees from Claudius\' expulsion of the Jews from Rome, they took Paul in because he was in the same trade. The Gentile mission began, in part, in a leatherworker\'s shop.',
      },
      {
        title: 'A word with Apollos',
        ref: 'Acts 18:24–26',
        text:
          'Apollos was eloquent, powerful in the scriptures, and knew only the baptism of John. They heard him in the synagogue, took him aside, and explained the way of God more accurately.',
      },
      {
        title: 'The church in their house',
        ref: 'Romans 16:3–5',
        text:
          'Paul greets them and the congregation that meets in their home, and calls them his fellow workers. In four of the six New Testament mentions her name comes first.',
      },
    ],
    verse: { ref: 'Romans 16:3', text: 'Greet Prisca and Aquila, my fellow workers in Christ Jesus.' },
    flaw:
      'Her whole story is bracketed by upheaval — thrown out of Rome by decree, rebuilding a workshop and a household in Corinth, then Ephesus, then Rome again — and we never once hear what she made of it.',
    legacy:
      'She is one of the clearest New Testament examples of a woman teaching a leading male preacher, which has made her a fixture in arguments about ministry ever since — though the text itself is matter-of-fact about it. The suggestion that she wrote the letter to the Hebrews is a modern conjecture with no ancient support, and nothing is recorded of her death.',
    portrait: {
      skin: 'olive',
      hair: 'curly',
      hairColour: 'brown',
      beard: 'none',
      headwear: 'none',
      robe: 'olive',
      emblem: 'tent',
    },
  },
  {
    id: 'lydia',
    name: 'Lydia',
    alsoKnownAs: '',
    epithet: 'Sold purple, opened her door',
    group: 'The Early Church',
    era: 'c. AD 50',
    testament: 'new',
    summary:
      'A businesswoman from Thyatira dealing in purple cloth — an expensive trade, which means she had money and a house of her own in Philippi. She was at a riverside prayer meeting on the Sabbath when Paul arrived, and Luke says the Lord opened her heart to listen. She is the first named convert on European soil, and she talked the missionaries into staying with her before they could decline politely.',
    moments: [
      {
        title: 'By the river',
        ref: 'Acts 16:13–14',
        text:
          'There was no synagogue, so a group of women met to pray outside the city gate by the water. Paul sat down and talked to them, and one of his listeners was a dealer in purple.',
      },
      {
        title: 'She persuaded us',
        ref: 'Acts 16:15',
        text:
          'Baptised with her household, she pressed her house on them: if they judged her faithful to the Lord, they should come and stay. Luke admits she had to persuade them.',
      },
      {
        title: 'Back to Lydia\'s house',
        ref: 'Acts 16:40',
        text:
          'Released from prison after the earthquake, Paul and Silas went straight to her home, saw the believers gathered there, encouraged them, and left. Her front room was the church in Philippi.',
      },
    ],
    verse: { ref: 'Acts 16:14', text: 'The Lord opened her heart to listen to the things which were spoken by Paul.' },
    flaw:
      'Nothing is recorded against her; what is recorded is that her hospitality had to be forced on reluctant guests, and that after one chapter she vanishes from the New Testament without another word.',
    legacy:
      'The congregation Paul later wrote to with such affection — the Philippians, who kept sending him money — began in her house. She is honoured as a saint in both Eastern and Western churches, but scripture says nothing whatever about the rest of her life.',
    portrait: {
      skin: 'light',
      hair: 'long',
      hairColour: 'black',
      beard: 'none',
      headwear: 'veil',
      robe: 'wine',
      emblem: 'cloak',
    },
  },
  {
    id: 'abraham',
    name: 'Abraham',
    alsoKnownAs: 'Abram',
    epithet: 'Left home on a promise',
    group: 'Old Testament',
    era: 'c. 2000 BC (traditional dating)',
    testament: 'old',
    summary:
      'A childless herdsman, born in Ur and settled in Haran, who packed up at seventy-five because a voice told him to go to a country it did not name. He spent the rest of his life under canvas in a land he had been promised but never owned — apart from one field with a cave in it, bought outright so he could bury his wife. Judaism, Christianity and Islam all trace themselves back to him.',
    moments: [
      {
        title: 'The call at Haran',
        ref: 'Genesis 12:1–5',
        text:
          'Settled, prosperous and seventy-five, he is told to leave his country, his kindred and his father\'s house for a land that is not specified. He goes — with Sarai, Lot, the servants and the herds — and never comes back.',
      },
      {
        title: 'Bargaining over Sodom',
        ref: 'Genesis 18:22–33',
        text:
          'Standing on the road before God, he haggles like a man in a market: fifty righteous, then forty-five, forty, thirty, twenty, ten, apologising each time for asking again. The city burns anyway.',
      },
      {
        title: 'The knife on Moriah',
        ref: 'Genesis 22:1–14',
        text:
          'Three days\' walk with the wood, the fire and the boy, and Isaac asking somewhere along the way where the lamb is. The knife is already raised when the voice stops him and he sees a ram caught by its horns in a thicket.',
      },
    ],
    verse: { ref: 'Genesis 15:6', text: 'He believed in Yahweh, who credited it to him for righteousness.' },
    flaw:
      'Twice he passed his wife off as his sister to a foreign ruler, buying his own safety with her, and when God finally named the year Isaac would be born he fell on his face and laughed.',
    legacy:
      'Scripture calls him the friend of God (Isaiah 41:8) and Paul makes him the pattern of faith for everyone outside the law (Romans 4). Genesis records his death at 175 and his burial beside Sarah in the cave at Machpelah; the vivid legends of his youth — smashing his father\'s idols, surviving a furnace — come from later Jewish midrash and the Qur\'anic tradition, not from the Bible.',
    portrait: {
      skin: 'tan',
      hair: 'receding',
      hairColour: 'white',
      beard: 'long',
      headwear: 'kaffiyeh',
      robe: 'sand',
      emblem: 'star',
    },
  },
  {
    id: 'moses',
    name: 'Moses',
    alsoKnownAs: 'Moshe',
    epithet: 'The stammerer who faced Pharaoh',
    group: 'Old Testament',
    era: 'c. 13th century BC (dating debated)',
    testament: 'old',
    summary:
      'A Hebrew raised in Pharaoh\'s household who killed an Egyptian he caught beating a Hebrew, buried him in the sand, and fled to spend forty years minding another man\'s sheep. A bush that burned without burning up sent him back, and he argued against the assignment for most of two chapters. He brought a nation out of Egypt, gave it a law, and died on a mountain looking at a country he was forbidden to enter.',
    moments: [
      {
        title: 'The bush that burned',
        ref: 'Exodus 3:1–4:13',
        text:
          'A thornbush alight and not consumed, out behind the wilderness at Horeb. He turns aside to look, is told to take his sandals off, and spends the rest of the conversation trying to get out of the job — he is slow of speech, he says, and always has been.',
      },
      {
        title: 'The tablets shattered',
        ref: 'Exodus 32:15–20',
        text:
          'He comes down the mountain with the stones in his arms, hears the singing before he sees the calf, and throws the tablets down at the foot of the hill. Then he burns the calf, grinds it to powder, scatters it on water and makes the people drink it.',
      },
      {
        title: 'The rock at Meribah',
        ref: 'Numbers 20:7–12',
        text:
          'Told to speak to the rock, he shouts at the crowd instead — ‘you rebels\' — and hits it twice with his staff. The water comes, and so does the sentence: he will not bring them into the land.',
      },
    ],
    verse: { ref: 'Exodus 3:14', text: 'God said to Moses, “I AM WHO I AM.”' },
    flaw:
      'His temper runs the whole length of his life — the Egyptian he killed and hid, the tablets he smashed, the rock he struck in fury — and in the end it was the temper, not any enemy, that shut him out of the land.',
    legacy:
      'Deuteronomy closes by saying no prophet has arisen in Israel like him, and the first five books of the Bible have carried his name since antiquity, though how much he wrote is long debated. Scripture records that he died in Moab at 120 and that no one knows his grave to this day; the tomb shown to travellers in the Judean desert is a much later tradition.',
    portrait: {
      skin: 'tan',
      hair: 'long',
      hairColour: 'white',
      beard: 'long',
      headwear: 'none',
      robe: 'clay',
      emblem: 'staff',
    },
  },
  {
    id: 'david',
    name: 'David',
    alsoKnownAs: 'Son of Jesse',
    epithet: 'Poet, killer, king, ruined father',
    group: 'Old Testament',
    era: 'c. 1010–970 BC',
    testament: 'old',
    summary:
      'The youngest of Jesse\'s eight sons, left out with the sheep when the prophet came to dinner, who became Israel\'s greatest king and its most quoted poet. He could spare a sleeping enemy in a cave and have a loyal officer killed by letter. The Bible tells both halves and does not tidy up the second.',
    moments: [
      {
        title: 'Five stones, no armour',
        ref: '1 Samuel 17:38–50',
        text:
          'He tries on Saul\'s armour, finds he cannot walk in it and takes it off, then stops at the brook for five smooth stones. Goliath is still shouting when the stone goes home.',
      },
      {
        title: 'Dancing before the ark',
        ref: '2 Samuel 6:14–22',
        text:
          'He dances the whole way up to Jerusalem in a linen ephod while his wife Michal watches from a window and despises him for it. He tells her he intends to be more undignified than this.',
      },
      {
        title: 'You are the man',
        ref: '2 Samuel 12:1–13',
        text:
          'Nathan describes a rich man who took a poor man\'s one ewe lamb, and the king, genuinely furious, says such a man deserves to die. Nathan lets him finish, then says: you are the man.',
      },
    ],
    verse: { ref: '1 Samuel 16:7', text: 'for man looks at the outward appearance, but Yahweh looks at the heart.' },
    flaw:
      'He saw a woman bathing, sent for her knowing whose wife she was, and when the pregnancy could not be concealed he had Uriah posted where the fighting was hardest and then abandoned there — and he was as passive with his own sons as he was ruthless with another man\'s wife and another man\'s life.',
    legacy:
      'Roughly half the psalms carry his name in their headings, and the promise of a throne without end runs from Samuel through the prophets to the opening line of Matthew\'s gospel. Kings records his death and burial in the city of David; the tomb shown on Mount Zion today is a medieval tradition rather than the biblical site.',
    portrait: {
      skin: 'olive',
      hair: 'curly',
      hairColour: 'auburn',
      beard: 'short',
      headwear: 'none',
      robe: 'wine',
      emblem: 'harp',
    },
  },
  {
    id: 'ruth',
    name: 'Ruth',
    alsoKnownAs: 'Ruth the Moabitess',
    epithet: 'A foreign widow who stayed',
    group: 'Old Testament',
    era: 'c. 1100 BC (the time of the judges)',
    testament: 'old',
    summary:
      'A Moabite woman, widowed young and formally released from any obligation, who refused to go home and walked instead into a country where her nationality was a liability. She kept herself and her mother-in-law alive by picking up what the harvesters dropped. Her great-grandson was David.',
    moments: [
      {
        title: 'Leaving Moab',
        ref: 'Ruth 1:14–18',
        text:
          'Orpah kisses Naomi goodbye and turns back, which is the sensible thing to do. Ruth holds on to her and answers the argument with a sentence that has outlived every other line in the book.',
      },
      {
        title: 'Gleaning behind the reapers',
        ref: 'Ruth 2:2–17',
        text:
          'She works from morning in a stranger\'s barley field, taking whatever the reapers miss, while Boaz quietly tells his men to pull stalks out of the bundles and leave them where she will find them. She beats out about an ephah of barley that evening.',
      },
      {
        title: 'The threshing floor',
        ref: 'Ruth 3:6–11',
        text:
          'She washes, dresses, and waits until Boaz has eaten and lain down at the end of the grain heap, then uncovers his feet and lies there. He wakes at midnight to find a woman asking him, in the politest possible terms, to marry her.',
      },
    ],
    verse: { ref: 'Ruth 1:16', text: 'where you go, I will go; and where you stay, I will stay.' },
    flaw:
      'Scripture records no fault in her, which is rare enough to be worth saying — what it records instead is her exposure: a Moabite widow with no land and no protector, eating at the goodwill of strangers, staking everything on a night-time approach that could just as easily have ruined her.',
    legacy:
      'The short book named for her is read at the Jewish festival of Shavuot, and Matthew puts her in the genealogy of Jesus — one of only four women named there. Scripture records nothing of her death; the domestic detail added by later retellings is invention.',
    portrait: {
      skin: 'tan',
      hair: 'long',
      hairColour: 'black',
      beard: 'none',
      headwear: 'veil',
      robe: 'olive',
      emblem: 'bread',
    },
  },
  {
    id: 'esther',
    name: 'Esther',
    alsoKnownAs: 'Hadassah',
    epithet: 'The queen who nearly refused',
    group: 'Old Testament',
    era: '5th century BC (reign of Xerxes I)',
    testament: 'old',
    summary:
      'A Jewish orphan raised by her cousin in the Persian capital, taken into the king\'s harem and crowned queen without anyone at court knowing what she was. When a decree went out to destroy every Jew in the empire, she was the one person who could get into the room. The book that carries her name never once mentions God.',
    moments: [
      {
        title: 'Word at the gate',
        ref: 'Esther 4:1–16',
        text:
          'Mordecai, in sackcloth outside the palace, sends word that she must go to the king. She sends back the law — anyone who approaches uninvited dies — and the fact that she has not been called for thirty days. Then she asks the city to fast three days, and says that if she perishes, she perishes.',
      },
      {
        title: 'Two banquets',
        ref: 'Esther 5:1–8; 7:1–6',
        text:
          'She invites the king and Haman to dinner, then invites them again for a second night, holding her request back each time. Only at the second table does she name the enemy, with him sitting across from her.',
      },
      {
        title: 'His own gallows',
        ref: 'Esther 7:9–10',
        text:
          'Haman had raised a pole fifty cubits high in his own courtyard, meaning it for Mordecai. A eunuch mentions this to the king in passing, and Haman is hanged on it the same day.',
      },
    ],
    verse: { ref: 'Esther 4:14', text: 'Who knows if you haven\'t come to the kingdom for such a time as this?' },
    flaw:
      'Her first answer was no — she quoted the law back at her cousin and left it at that, and it took a blunt reminder that the palace would not save her before she agreed to risk the walk to the throne room.',
    legacy:
      'Her story is the charter of Purim, still kept each spring with the reading of the scroll, noise at Haman\'s name, gifts of food and money to the poor. Scripture records no death for her; the shrine at Hamadan venerated as her tomb and Mordecai\'s belongs to a much later tradition.',
    portrait: {
      skin: 'olive',
      hair: 'long',
      hairColour: 'black',
      beard: 'none',
      headwear: 'crown',
      robe: 'indigo',
      emblem: 'scroll',
    },
  },
  {
    id: 'elijah',
    name: 'Elijah',
    alsoKnownAs: 'Elijah the Tishbite',
    epithet: 'Fire on Carmel, despair after',
    group: 'Old Testament',
    era: '9th century BC',
    testament: 'old',
    summary:
      'A rough prophet out of Gilead who walked into Ahab\'s court, announced a drought, and then disappeared — fed first by ravens at a drying brook, then for the rest of it by a foreign widow in Sidon who was down to her last handful of meal. He humiliated four hundred and fifty prophets of Baal in a single afternoon and asked to die the following day. He is one of only two figures in the Hebrew Bible who are never said to have died.',
    moments: [
      {
        title: 'The contest on Carmel',
        ref: '1 Kings 18:20–39',
        text:
          'Two bulls, two altars, no fire lit. He lets Baal\'s prophets call from morning to noon, then at noon suggests their god may be deep in thought, or away, or asleep — after which they cry louder and cut themselves with knives until the blood runs, on into the evening. Then he drenches his own altar with four jars of water, three times over, and prays once.',
      },
      {
        title: 'Under the broom tree',
        ref: '1 Kings 19:1–8',
        text:
          'One threatening message from Jezebel and he runs a day into the wilderness, sits down under a broom tree and asks to die. What he is given is sleep, a cake baked on hot stones and a jar of water — twice.',
      },
      {
        title: 'The whirlwind at Jordan',
        ref: '2 Kings 2:8–14',
        text:
          'He rolls up his cloak, strikes the river with it and walks across dry ground beside Elisha. A chariot of fire comes between them and he goes up in the wind, leaving the cloak on the bank.',
      },
    ],
    verse: { ref: '1 Kings 19:12', text: 'After the fire, there was a still small voice.' },
    flaw:
      'After the greatest public victory of his life he folded into self-pity and told God twice that he alone was left — which was flatly untrue, as the answer pointed out: seven thousand had never bowed to Baal.',
    legacy:
      'Malachi promises he will return before the day of the Lord, and the gospels place him beside Moses at the Transfiguration. Scripture gives him no death and no grave; the cup set for him at the Passover table is a Jewish custom of much later date rather than a biblical instruction.',
    portrait: {
      skin: 'tan',
      hair: 'long',
      hairColour: 'grey',
      beard: 'full',
      headwear: 'none',
      robe: 'slate',
      emblem: 'fire',
    },
  },
  {
    id: 'isaiah',
    name: 'Isaiah',
    alsoKnownAs: 'Isaiah son of Amoz',
    epithet: 'Man of unclean lips',
    group: 'Old Testament',
    era: '8th century BC (c. 740–690 BC)',
    testament: 'old',
    summary:
      'A prophet with the ear of kings, working in Jerusalem across four reigns and telling at least two of those kings what they least wanted to hear. He once went stripped and barefoot for three years as a walking warning against trusting Egypt. The book under his name holds some of the most quoted lines in the English language.',
    moments: [
      {
        title: 'The year Uzziah died',
        ref: 'Isaiah 6:1–8',
        text:
          'He sees the Lord enthroned with the hem of his robe filling the temple and creatures calling to one another until the doorposts shake. His first reaction is not wonder but horror at his own mouth, and a live coal off the altar is pressed to it.',
      },
      {
        title: 'Barefoot for three years',
        ref: 'Isaiah 20:1–4',
        text:
          'Told to take the sackcloth off his body and the sandals off his feet, he does it — and keeps doing it for three years, walking Jerusalem as a public picture of how Egypt\'s captives would be marched away.',
      },
      {
        title: 'The siege lifted',
        ref: 'Isaiah 37:14–36',
        text:
          'Hezekiah carries the Assyrian ultimatum into the temple and spreads it out. Isaiah sends back word that Sennacherib will not shoot so much as an arrow into the city, and by morning the camp outside is full of the dead.',
      },
    ],
    verse: { ref: 'Isaiah 6:8', text: 'Then I said, “Here I am. Send me!”' },
    flaw:
      'His own verdict came first and was the harshest anyone gave him — a man of unclean lips among a people of unclean lips — and the commission that followed was to preach to people whose ears he was told would not hear; when he finally carried word to Hezekiah that the king\'s own sons would be carried off to Babylon, he let Hezekiah\'s shrug — at least there would be peace in his own days — stand without answering it.',
    legacy:
      'No prophet is quoted more often in the New Testament, and the Great Isaiah Scroll from Qumran — a thousand years older than any copy previously known — is the most famous manuscript of the Hebrew Bible. Whether chapters 40–66 come from him or from later voices in his tradition is a long-standing scholarly argument; the story that Manasseh had him sawn in two is Jewish and Christian tradition, not biblical record.',
    portrait: {
      skin: 'olive',
      hair: 'short',
      hairColour: 'grey',
      beard: 'full',
      headwear: 'turban',
      robe: 'ivory',
      emblem: 'fire',
    },
  },
  {
    id: 'daniel',
    name: 'Daniel',
    alsoKnownAs: 'Belteshazzar',
    epithet: 'Prayed with the windows open',
    group: 'Old Testament',
    era: '6th century BC',
    testament: 'old',
    summary:
      'A boy from Jerusalem\'s nobility deported to Babylon, renamed after a Babylonian god and trained to administer the empire that had sacked his city. He read Nebuchadnezzar\'s dreams, read the writing on Belshazzar\'s wall, and was still in office under Darius and Cyrus — serving across the fall of one empire into the next. He is remembered for a night among lions and for a hand that wrote on a wall.',
    moments: [
      {
        title: 'Vegetables and water',
        ref: 'Daniel 1:8–16',
        text:
          'Offered the king\'s food and wine, he asks the steward for a ten-day trial on pulses and water instead, quietly refusing the first thing the empire wants to give him. At the end of it he and his three friends look better fed than the boys on the royal ration.',
      },
      {
        title: 'Writing on the wall',
        ref: 'Daniel 5:5–28',
        text:
          'Fingers appear during Belshazzar\'s feast and write four words on the plaster of the palace wall, and the king\'s knees knock together. Daniel, sent for as a last resort, tells him to keep his gifts and reads the words as a ledger: weighed, found wanting, divided.',
      },
      {
        title: 'Windows towards Jerusalem',
        ref: 'Daniel 6:10–23',
        text:
          'The decree is signed, so he goes home, opens the upstairs windows towards Jerusalem and kneels three times that day exactly as before, knowing he is watched. The king, who cannot revoke his own law, spends the night fasting beside a sealed den.',
      },
    ],
    verse: { ref: 'Daniel 6:22', text: 'My God has sent his angel, and has shut the lions\' mouths.' },
    flaw:
      'The furnace was his friends\' ordeal, not his; his own recorded struggle is fear — the visions left him drained, ill for days and openly admitting he could not understand what he had been shown.',
    legacy:
      'Ezekiel names him alongside Noah and Job as a byword for righteousness, and his visions of a kingdom that outlasts empires shaped Jewish and Christian expectation ever after. When the book reached its final form is one of the long-running scholarly arguments; his death is nowhere recorded in scripture.',
    portrait: {
      skin: 'olive',
      hair: 'short',
      hairColour: 'black',
      beard: 'stubble',
      headwear: 'cap',
      robe: 'indigo',
      emblem: 'lion',
    },
  },
  {
    id: 'joseph',
    name: 'Joseph son of Jacob',
    alsoKnownAs: 'Zaphenath-Paneah',
    epithet: 'Sold by brothers, fed them',
    group: 'Old Testament',
    era: 'patriarchal period, c. 1800 BC (dating uncertain)',
    testament: 'old',
    summary:
      'The favourite son of a favourite wife, and insufferable about it, sold into Egypt by his own brothers and worked up from a prison cell to running the country. When famine finally drove those brothers south to buy grain, he was the official they had to stand in front of and ask. He fed them.',
    moments: [
      {
        title: 'The pit at Dothan',
        ref: 'Genesis 37:18–28',
        text:
          'They see the coat coming across the fields and decide to kill him; Reuben talks them down to an empty cistern. They sit down to eat with him in it, then sell him to a passing caravan for twenty pieces of silver.',
      },
      {
        title: 'The cloak left behind',
        ref: 'Genesis 39:7–20',
        text:
          'Potiphar\'s wife catches hold of his garment and he walks out of the house without it. The cloth in her hand becomes the whole case against him, and he goes to prison without a hearing.',
      },
      {
        title: 'I am Joseph',
        ref: 'Genesis 45:1–4',
        text:
          'He clears the room of every Egyptian and then weeps so loudly that the household hears it through the wall. He tells his brothers who he is, and they cannot get a word out.',
      },
    ],
    verse: { ref: 'Genesis 50:20', text: 'you meant evil against me, but God meant it for good.' },
    flaw:
      'As a boy he carried bad reports of his brothers to their father and told them, twice, about dreams in which they bowed to him — and as Egypt\'s administrator he ran a famine policy that ended with the population selling first their livestock, then their land, then themselves to Pharaoh, which Genesis reports without a word of comment.',
    legacy:
      'His story is the longest continuous narrative about any one person in Genesis, and two of Israel\'s tribes, Ephraim and Manasseh, carry his sons\' names. Genesis records his death at 110 and his embalming in Egypt; Exodus and Joshua then follow his bones out of the country to a grave at Shechem.',
    portrait: {
      skin: 'tan',
      hair: 'short',
      hairColour: 'black',
      beard: 'none',
      headwear: 'none',
      robe: 'ochre',
      emblem: 'cloak',
    },
  },
  {
    id: 'deborah',
    name: 'Deborah',
    alsoKnownAs: 'Wife of Lappidoth',
    epithet: 'Judge, prophet, war poet',
    group: 'Old Testament',
    era: 'c. 12th century BC (the time of the judges)',
    testament: 'old',
    summary:
      'The only woman among Israel\'s judges, holding court under a palm tree in the hill country while people walked up from the villages to have their disputes settled. When the time came to deal with Sisera\'s nine hundred iron chariots she named the commander, the mountain and the day — then went along herself, because the commander would not go without her. The victory song in Judges 5 — sung, the text says, by Deborah and Barak together, and known ever since by her name — is one of the oldest things in the Bible.',
    moments: [
      {
        title: 'Under the palm',
        ref: 'Judges 4:4–5',
        text:
          'She sits between Ramah and Bethel in the hill country of Ephraim, and Israel comes up to her for judgement. The tree keeps her name.',
      },
      {
        title: 'Barak will not go',
        ref: 'Judges 4:6–10',
        text:
          'She summons Barak and gives him his orders: ten thousand men from Naphtali and Zebulun, up Mount Tabor. He says he will go only if she goes, and she agrees — with the warning that the honour of the day will now belong to a woman.',
      },
      {
        title: 'Stars and torrent',
        ref: 'Judges 5:20–28',
        text:
          'Her song has the stars fighting from their courses and the Kishon in flood sweeping the chariots away. It ends at a lattice window, with Sisera\'s mother wondering aloud why the wheels of his chariot are so long in coming.',
      },
    ],
    verse: { ref: 'Judges 5:7', text: 'until I, Deborah, arose; until I arose a mother in Israel.' },
    flaw:
      'The hardness is in the song rather than the story — it curses the town of Meroz for staying home, mocks the tribes that sat comfortably among the sheepfolds, and lingers with something close to relish over the mother at the window who will not see her son again.',
    legacy:
      'The Song of Deborah is widely regarded as one of the oldest surviving pieces of Hebrew poetry, and the land had rest for forty years after her. Scripture says nothing at all about her death; the palm remembered by name is the only monument it gives her.',
    portrait: {
      skin: 'olive',
      hair: 'long',
      hairColour: 'grey',
      beard: 'none',
      headwear: 'none',
      robe: 'clay',
      emblem: 'harp',
    },
  },
];

export const CHARACTER_GROUPS = [...new Set(CHARACTERS.map((c) => c.group))];

export const CHARACTERS_BY_ID = Object.fromEntries(CHARACTERS.map((c) => [c.id, c]));
